import React, { useState, useEffect } from 'react';
import { X, User, Calendar, Save, XCircle, AlertCircle, Edit3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface ProfessorModalProps {
  professor: any | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (professorData: any) => Promise<void>;
}

// Funções de máscara
const maskCPF = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    .substring(0, 14);
};

const maskPhone = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{4,5})(\d{4})$/, '$1-$2')
    .substring(0, 15);
};

const ProfessorModal: React.FC<ProfessorModalProps> = ({ professor, isOpen, onClose, onSave }) => {
  const { userProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(!professor);
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (professor) {
      setFormData({
        nome: professor.nome || '',
        email: professor.email || '',
        telefone: professor.telefone || '',
        cellular: professor.cellular || '',
        cpf: professor.cpf || '',
        dataNascimento: professor.dataNascimento || '',
        bAtivo: professor.bAtivo !== undefined ? professor.bAtivo : true
      });
      setIsEditing(false);
    } else {
      setFormData({
        nome: '',
        email: '',
        telefone: '',
        cellular: '',
        cpf: '',
        dataNascimento: '',
        bAtivo: true
      });
      setIsEditing(true);
    }
    setErrors({});
  }, [professor, isOpen]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateCPF = (cpf: string): boolean => {
    if (!cpf) return true;
    const cleanCPF = cpf.replace(/\D/g, '');
    return cleanCPF.length === 11;
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true;
    const cleanPhone = phone.replace(/\D/g, '');
    return cleanPhone.length >= 10;
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome || formData.nome.trim() === '') {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = 'Email é obrigatório';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.telefone && !formData.cellular) {
      newErrors.telefone = 'Telefone ou celular é obrigatório';
    }

    if (formData.telefone && !validatePhone(formData.telefone)) {
      newErrors.telefone = 'Telefone inválido';
    }

    if (formData.cellular && !validatePhone(formData.cellular)) {
      newErrors.cellular = 'Celular inválido';
    }

    if (formData.cpf && !validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: any) => {
    let processedValue = value;

    if (field === 'cpf') {
      processedValue = maskCPF(value);
    } else if (field === 'telefone' || field === 'cellular') {
      processedValue = maskPhone(value);
    }

    setFormData((prev: any) => ({
      ...prev,
      [field]: processedValue
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };


  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    try {
      setSaving(true);
      await onSave({
        ...(professor || {}),
        ...formData
      });
      setIsEditing(false);
      if (!professor) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar professor:', error);
      setErrors({ submit: 'Erro ao salvar professor. Tente novamente.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (professor) {
      setFormData({
        nome: professor.nome || '',
        email: professor.email || '',
        telefone: professor.telefone || '',
        cellular: professor.cellular || '',
        cpf: professor.cpf || '',
        dataNascimento: professor.dataNascimento || '',
        bAtivo: professor.bAtivo !== undefined ? professor.bAtivo : true
      });
      setIsEditing(false);
    } else {
      onClose();
    }
    setErrors({});
  };

  if (!isOpen) return null;

  const isCreating = !professor;
  const displayName = professor?.nome || 'Novo Professor';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').substring(0, 2);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                {professor?.fotoUrl ? (
                  <img 
                    src={professor.fotoUrl} 
                    alt={displayName}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-medium text-primary-600">
                    {initials}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {isCreating ? 'Novo Professor' : (isEditing ? 'Editar Professor' : 'Detalhes do Professor')}
                </h2>
                {professor && <p className="text-sm text-gray-500 dark:text-gray-400">ID: {professor.id}</p>}
              </div>
            </div>
            <div className="flex items-center gap-x-2">
              {!isEditing && !isCreating ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  Editar
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                    disabled={saving}
                  >
                    <XCircle className="h-4 w-4" />
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors flex items-center gap-2"
                    disabled={saving}
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={saving}
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>{errors.submit}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <User className="h-5 w-5" />
                Informações Pessoais
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome Completo <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => handleInputChange('nome', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.nome ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Nome completo do professor"
                    />
                    {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{professor.nome || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="email@exemplo.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{professor.email || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefone
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="tel"
                      value={formData.telefone}
                      onChange={(e) => handleInputChange('telefone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.telefone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="(00) 0000-0000"
                    />
                    {errors.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{professor.telefone || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Celular
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="tel"
                      value={formData.cellular}
                      onChange={(e) => handleInputChange('cellular', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.cellular ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="(00) 00000-0000"
                    />
                    {errors.cellular && <p className="text-red-500 text-xs mt-1">{errors.cellular}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{professor.cellular || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CPF
                </label>
                {isEditing || isCreating ? (
                  <>
                    <input
                      type="text"
                      value={formData.cpf}
                      onChange={(e) => handleInputChange('cpf', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.cpf ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                    {errors.cpf && <p className="text-red-500 text-xs mt-1">{errors.cpf}</p>}
                  </>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{professor.cpf || 'Não informado'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data de Nascimento
                </label>
                {isEditing || isCreating ? (
                  <input
                    type="date"
                    value={formData.dataNascimento}
                    onChange={(e) => handleInputChange('dataNascimento', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">
                    {professor?.dataNascimento ? new Date(professor.dataNascimento).toLocaleDateString('pt-BR') : 'Não informado'}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-x-2">
                <Calendar className="h-5 w-5" />
                Informações Profissionais
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                {isEditing || isCreating ? (
                  <select
                    value={formData.bAtivo ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('bAtivo', e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent max-w-xs"
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                ) : (
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    professor?.bAtivo 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {professor?.bAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessorModal;

